import { AppliedSettingsRow } from './AppliedSettingsRow';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { Stack, FlexBox } from '@ardoq/layout';
import { Tag } from '@ardoq/status-ui';
import { WithPopover } from '@ardoq/popovers';
import { colors } from '@ardoq/design-tokens';
import styled from 'styled-components';
import {
  RuleDescription,
  RuleDescriptionsByEntity,
  FormattingSetting,
} from './types';
import { t, type I18nLocale } from '@ardoq/i18n';

const ColorSwatch = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 2px;
`;

type InfoBlockProps = {
  entityName: string;
  iconName: IconName;
  ruleDescriptions: RuleDescription[];
};
const InfoBlock = ({
  entityName,
  iconName,
  ruleDescriptions,
}: InfoBlockProps) => (
  <>
    <FlexBox gap="xsmall" align="center">
      <Icon
        iconName={iconName}
        iconSize={IconSize.SMALL}
        color={colors.grey500}
        // Passing inline style for the Tag SVG icon
        style={{ color: colors.grey500 }}
      />
      {entityName}
    </FlexBox>
    <FlexBox flexDirection="column" gap="small" paddingLeft="medium">
      {ruleDescriptions.map(({ color, filterDescription }) => (
        <FlexBox gap="xsmall" key={filterDescription} align="center">
          <ColorSwatch style={{ backgroundColor: color }} />
          {filterDescription}
        </FlexBox>
      ))}
    </FlexBox>
  </>
);

const buildPopoverContent = (
  { components, references, tags }: RuleDescriptionsByEntity,
  i18nLocale: I18nLocale
) => (
  <Stack gap="small">
    {components && (
      <InfoBlock
        entityName={t(i18nLocale, 'appLayout:formattingComponent', 'Component')}
        iconName={IconName.COMPONENT}
        ruleDescriptions={components}
      />
    )}
    {references && (
      <InfoBlock
        entityName={t(i18nLocale, 'appLayout:formattingReference', 'Reference')}
        iconName={IconName.REFERENCE}
        ruleDescriptions={references}
      />
    )}
    {tags && (
      <InfoBlock
        entityName={t(i18nLocale, 'appLayout:formattingTag', 'Tag')}
        iconName={IconName.TAG}
        ruleDescriptions={tags}
      />
    )}
  </Stack>
);

type FormattingSettingsProps = {
  settings: FormattingSetting;
  i18nLocale: I18nLocale;
};
export const FormattingSettings = ({
  settings,
  i18nLocale,
}: FormattingSettingsProps) => {
  const { appliedRulesCount, ruleDescriptionsByEntity, onEditClick } = settings;

  const popoverContent = appliedRulesCount
    ? buildPopoverContent(ruleDescriptionsByEntity, i18nLocale)
    : null;

  const tag = (
    <WithPopover content={() => popoverContent} key="FormattingTag">
      <Tag label={appliedRulesCount.toString()} />
    </WithPopover>
  );

  return (
    <AppliedSettingsRow
      label={t(i18nLocale, 'appLayout:formatsLabel', 'Formats')}
      tags={[tag]}
      icon={IconName.PALETTE}
      onEditClick={onEditClick}
      isDisabled={!appliedRulesCount}
      i18nLocale={i18nLocale}
    />
  );
};
